export interface PatreonTierBenefit {
  tierName: string;
  extraGarageSlotsPerSpecies: number;
  storeDiscountPercent: number;
  playtimeRewardPercent: number;
  maxPoints?: number;
}

export const theNestPatreonBenefits: readonly PatreonTierBenefit[] = [
  { tierName: "Hatchling", extraGarageSlotsPerSpecies: 1, storeDiscountPercent: 0, playtimeRewardPercent: 0 },
  { tierName: "Juvenile", extraGarageSlotsPerSpecies: 2, storeDiscountPercent: 10, playtimeRewardPercent: 50, maxPoints: 125_000 },
  { tierName: "Sub-Adult", extraGarageSlotsPerSpecies: 3, storeDiscountPercent: 15, playtimeRewardPercent: 75, maxPoints: 150_000 },
  { tierName: "Adult", extraGarageSlotsPerSpecies: 5, storeDiscountPercent: 20, playtimeRewardPercent: 100, maxPoints: 200_000 },
  { tierName: "Prime Elder", extraGarageSlotsPerSpecies: 10, storeDiscountPercent: 30, playtimeRewardPercent: 150, maxPoints: 300_000 }
] as const;

/** @deprecated Use theNestPatreonBenefits. */
export const sunnyIslePatreonBenefits = theNestPatreonBenefits;

export function resolvePatreonTierBenefit(tierName: string | undefined): PatreonTierBenefit | undefined {
  const normalized = tierName?.trim().toLowerCase();
  if (!normalized) return undefined;
  return theNestPatreonBenefits.find((benefit) => benefit.tierName.toLowerCase() === normalized);
}

export function isPatreonTierAtLeast(tierName: string | undefined, minimumTierName: string): boolean {
  const current = resolvePatreonTierBenefit(tierName);
  const minimum = resolvePatreonTierBenefit(minimumTierName);
  if (!current || !minimum) return false;
  return theNestPatreonBenefits.indexOf(current) >= theNestPatreonBenefits.indexOf(minimum);
}

export function resolvePatreonStoreDiscountPercent(tierName: string | undefined, fallbackPercent: number): number {
  return resolvePatreonTierBenefit(tierName)?.storeDiscountPercent ?? normalizePercent(fallbackPercent);
}

export function resolvePatreonPlaytimeReward(basePoints: number, tierName: string | undefined): number {
  const normalizedBase = Math.max(0, Math.floor(Number.isFinite(basePoints) ? basePoints : 0));
  const increase = resolvePatreonTierBenefit(tierName)?.playtimeRewardPercent ?? 0;
  return Math.floor(normalizedBase * (100 + increase) / 100);
}

/**
 * Staff earn at least the Adult playtime rate without inheriting any other
 * Patreon benefit. A genuinely higher Patreon tier still wins.
 */
export function resolveStaffPlaytimeReward(
  basePoints: number,
  tierName: string | undefined,
  staff: boolean
): number {
  const memberReward = resolvePatreonPlaytimeReward(basePoints, tierName);
  return staff
    ? Math.max(memberReward, resolvePatreonPlaytimeReward(basePoints, "Adult"))
    : memberReward;
}

export function withTheNestGarageTierDefaults(policy: GarageCapacityPolicy): GarageCapacityPolicy {
  const existing = new Set(policy.tierLimits.map((entry) => entry.tierName.trim().toLowerCase()));
  return {
    ...policy,
    tierLimits: [
      ...policy.tierLimits,
      ...theNestPatreonBenefits
        .filter((benefit) => !existing.has(benefit.tierName.toLowerCase()))
        .map((benefit) => ({
          tierName: benefit.tierName,
          perSpecies: Math.min(100, policy.defaultPerSpecies + benefit.extraGarageSlotsPerSpecies)
        }))
    ]
  };
}

export function withTheNestPointCapTierDefaults(policy: PointBalancePolicy): PointBalancePolicy {
  const existing = new Set(policy.tierLimits.map((entry) => entry.tierName.trim().toLowerCase()));
  return {
    ...policy,
    tierLimits: [
      ...policy.tierLimits,
      ...theNestPatreonBenefits.flatMap((benefit) =>
        benefit.maxPoints === undefined || existing.has(benefit.tierName.toLowerCase())
          ? []
          : [{ tierName: benefit.tierName, maxPoints: benefit.maxPoints }]
      )
    ]
  };
}

/** @deprecated Use withTheNestGarageTierDefaults. */
export const withSunnyIsleGarageTierDefaults = withTheNestGarageTierDefaults;

/** @deprecated Use withTheNestPointCapTierDefaults. */
export const withSunnyIslePointCapTierDefaults = withTheNestPointCapTierDefaults;

function normalizePercent(value: number): number {
  return Math.max(0, Math.min(100, Math.trunc(Number.isFinite(value) ? value : 0)));
}
import type { PointBalancePolicy } from "./economy.js";
import type { GarageCapacityPolicy } from "./garage.js";
